/**
 * PetrifySkillEffect — Earth tree sculptor-path apotheosis.
 *
 * Instant activation. Become stone for 5 seconds:
 *   - Entrance burst: 24 AoE damage + 1.5-tile knockback in a 3-tile radius.
 *   - Petrified state: 60% DR, knockback/stagger immune, completely immobile.
 *   - Exit burst: 18 AoE damage in a 2-tile radius (no knockback).
 *
 * State machine: idle -> active (5000ms) -> cooldown (4000ms) -> idle.
 * The active phase is non-cancellable — deactivate() is a no-op while
 * petrified. Cooldown begins when the stone cracks off, not on activation.
 *
 * Two bursts — entrance and exit — bookending an impenetrable 5-second
 * fortress state. You stop being a person for five seconds. What stands
 * in your place is worse for everyone nearby.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see earth-spec.md § Petrify — Apotheosis
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface PetrifyDeps {
  readonly player: {
    getPosition(): { x: number; y: number };
  };
  readonly targeting: {
    getEnemiesInRadius(radiusPx: number): string[];
    getEnemyPosition(enemyId: string): { x: number; y: number };
  };
  readonly combat: {
    applyDamage(
      targetId: string,
      damage: number,
      opts: { sourceSkillId: string },
    ): void;
    applyKnockback(
      targetId: string,
      normalizedDir: { x: number; y: number },
      tiles: number,
    ): void;
  };
  readonly playerState: {
    setPetrified(active: boolean): void;
    setDamageReduction(sourceSkillId: string, value: number): void;
    setKnockbackImmune(sourceSkillId: string, immune: boolean): void;
    setStaggerImmune(sourceSkillId: string, immune: boolean): void;
    setMovementLocked(sourceSkillId: string, locked: boolean): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface PetrifyConfig {
  /** Duration of the petrified state in ms. */
  readonly activeDurationMs: number;
  /** Very_long cooldown tier — starts when petrification ends. */
  readonly cooldownMs: number;
  /** Entrance burst: AoE damage. */
  readonly entranceDamage: number;
  /** Entrance burst: radius in tiles. */
  readonly entranceRadiusTiles: number;
  /** Entrance burst: knockback distance in tiles. */
  readonly entranceKnockbackTiles: number;
  /** Exit burst: AoE damage. */
  readonly exitDamage: number;
  /** Exit burst: radius in tiles. */
  readonly exitRadiusTiles: number;
  /** Flat damage reduction while petrified (0.6 = 60%). */
  readonly damageReduction: number;
  /** World units per tile. */
  readonly tileSizePx: number;
}

const DEFAULT_CONFIG: PetrifyConfig = {
  activeDurationMs: 5000,              // 5s petrified
  cooldownMs: 4000,                    // very_long cooldown tier
  entranceDamage: 24,
  entranceRadiusTiles: 3,
  entranceKnockbackTiles: 1.5,
  exitDamage: 18,
  exitRadiusTiles: 2,
  damageReduction: 0.6,
  tileSizePx: 32,
};

// ---------------------------------------------------------------------------
// Phase type
// ---------------------------------------------------------------------------

export type PetrifyPhase = 'idle' | 'active' | 'cooldown';

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class PetrifySkillEffect implements SkillEffect {
  readonly skillId = 'petrify';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: PetrifyDeps;
  private readonly config: PetrifyConfig;

  private _phase: PetrifyPhase = 'idle';
  private _phaseTimer = 0;

  // Telemetry for scenario validation
  private _activationCount = 0;
  private _entranceDamageTargets = 0;
  private _exitDamageTargets = 0;

  constructor(deps: PetrifyDeps, config: Partial<PetrifyConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get phase(): PetrifyPhase { return this._phase; }
  get activationCount(): number { return this._activationCount; }
  get entranceDamageTargets(): number { return this._entranceDamageTargets; }
  get exitDamageTargets(): number { return this._exitDamageTargets; }
  get damageReduction(): number { return this.config.damageReduction; }
  get entranceRadiusTiles(): number { return this.config.entranceRadiusTiles; }
  get exitRadiusTiles(): number { return this.config.exitRadiusTiles; }

  get cooldown(): number {
    return this._phase === 'cooldown' ? this._phaseTimer : 0;
  }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._phase === 'idle';
  }

  activate(): boolean {
    if (this._phase !== 'idle') return false;

    this._phase = 'active';
    this._phaseTimer = this.config.activeDurationMs;
    this._activationCount++;
    this._entranceDamageTargets = 0;
    this._exitDamageTargets = 0;

    // Set petrified state: movement lock, DR, immunities
    this.deps.playerState.setPetrified(true);
    this.deps.playerState.setMovementLocked('petrify', true);
    this.deps.playerState.setDamageReduction('petrify', this.config.damageReduction);
    this.deps.playerState.setKnockbackImmune('petrify', true);
    this.deps.playerState.setStaggerImmune('petrify', true);

    // Fire entrance burst
    this._fireEntranceBurst();

    return true;
  }

  /** Non-cancellable: petrification cannot be interrupted by the player. */
  deactivate(): void {
    // Intentionally empty — the stone holds for its full duration.
  }

  isActive(): boolean {
    return this._phase === 'active';
  }

  update(delta: number): void {
    if (this._phase === 'idle') return;

    this._phaseTimer -= delta;

    if (this._phase === 'active' && this._phaseTimer <= 0) {
      this._onPetrificationEnd();
      this._phase = 'cooldown';
      this._phaseTimer = this.config.cooldownMs;
      return;
    }

    if (this._phase === 'cooldown' && this._phaseTimer <= 0) {
      this._phase = 'idle';
      this._phaseTimer = 0;
    }
  }

  destroy(): void {
    if (this._phase === 'active') {
      this._clearPetrifiedState();
    }
    this._phase = 'idle';
    this._phaseTimer = 0;
  }

  // --- Internal ---

  private _fireEntranceBurst(): void {
    const { x, y } = this.deps.player.getPosition();
    const radiusPx = this.config.entranceRadiusTiles * this.config.tileSizePx;
    const candidates = this.deps.targeting.getEnemiesInRadius(radiusPx);
    this._entranceDamageTargets = 0;

    for (const enemyId of candidates) {
      const pos = this.deps.targeting.getEnemyPosition(enemyId);
      const dx = pos.x - x;
      const dy = pos.y - y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist <= radiusPx) {
        this.deps.combat.applyDamage(enemyId, this.config.entranceDamage, {
          sourceSkillId: 'petrify',
        });

        // Knockback direction: away from player
        const nx = dist > 0 ? dx / dist : 0;
        const ny = dist > 0 ? dy / dist : 1;

        this.deps.combat.applyKnockback(
          enemyId,
          { x: nx, y: ny },
          this.config.entranceKnockbackTiles,
        );

        this._entranceDamageTargets++;
      }
    }
  }

  private _fireExitBurst(): void {
    const { x, y } = this.deps.player.getPosition();
    const radiusPx = this.config.exitRadiusTiles * this.config.tileSizePx;
    const candidates = this.deps.targeting.getEnemiesInRadius(radiusPx);
    this._exitDamageTargets = 0;

    for (const enemyId of candidates) {
      const pos = this.deps.targeting.getEnemyPosition(enemyId);
      const dx = pos.x - x;
      const dy = pos.y - y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist <= radiusPx) {
        // Exit burst: damage only, no knockback
        this.deps.combat.applyDamage(enemyId, this.config.exitDamage, {
          sourceSkillId: 'petrify',
        });
        this._exitDamageTargets++;
      }
    }
  }

  private _clearPetrifiedState(): void {
    this.deps.playerState.setPetrified(false);
    this.deps.playerState.setMovementLocked('petrify', false);
    this.deps.playerState.setDamageReduction('petrify', 0);
    this.deps.playerState.setKnockbackImmune('petrify', false);
    this.deps.playerState.setStaggerImmune('petrify', false);
  }

  private _onPetrificationEnd(): void {
    this._fireExitBurst();
    this._clearPetrifiedState();
  }
}
